<script lang="ts">
  import Icon from '../components/Icon.svelte';
  import { onMount } from 'svelte';
  import { replace, pop } from 'svelte-spa-router';
  import { session } from '../lib/session';
  import { api } from '../lib/api';
  import { formatDate, formatDateTime } from '../lib/format';
  import type {
    AdminLedgerEntryDTO,
    AdminPendingPaymentDTO,
    AdminReferralDetailDTO,
    AdminReferrerDTO,
  } from '../lib/types';

  let referrers = $state<AdminReferrerDTO[]>([]);
  let truncated = $state(false);
  let pending = $state<AdminPendingPaymentDTO[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);

  // Драбина в журнал одного контакту — модалкою, щоб не втрачати зведення.
  let detail = $state<AdminReferralDetailDTO | null>(null);
  let detailLoading = $state(false);
  let busyPaymentId = $state<string | null>(null);

  // Ручна корекція
  let adjustAmount = $state('');
  let adjustNote = $state('');
  let adjustBusy = $state(false);
  let adjustError = $state<string | null>(null);

  const TYPE_LABEL: Record<AdminLedgerEntryDTO['type'], string> = {
    accrual: 'Нарахування',
    reversal: 'Сторно',
    spend: 'Списання',
    adjustment: 'Корекція',
  };

  const ACCRUE_ERROR: Record<string, string> = {
    no_referrer: 'У покупця немає реферера',
    already_accrued: 'Комісію за цю людину вже нараховано',
    not_succeeded: 'Оплата не в статусі «успішна»',
    zero_base: 'Нічого нараховувати — сума після бонусів нульова',
    not_found: 'Оплату не знайдено',
  };

  function goBack(): void {
    if (window.history.length > 1) pop();
    else replace('/admin/settings');
  }

  async function load(): Promise<void> {
    try {
      const [list, review] = await Promise.all([
        api.adminReferrers(),
        api.adminReferralPendingReview(),
      ]);
      referrers = list.items;
      truncated = list.truncated;
      pending = review.items;
    } catch {
      error = 'Не вдалось завантажити зведення';
    } finally {
      loading = false;
    }
  }

  async function openDetail(telegramId: string): Promise<void> {
    detailLoading = true;
    adjustAmount = '';
    adjustNote = '';
    adjustError = null;
    try {
      detail = await api.adminReferralDetail(telegramId);
    } catch {
      error = 'Не вдалось завантажити журнал контакту';
    } finally {
      detailLoading = false;
    }
  }

  async function accrue(p: AdminPendingPaymentDTO): Promise<void> {
    busyPaymentId = p.paymentId;
    error = null;
    try {
      await api.adminReferralAccrue(p.paymentId);
      await load();
    } catch (err) {
      const code = (err as { code?: string }).code ?? '';
      error = ACCRUE_ERROR[code] ?? 'Не вдалось нарахувати';
    } finally {
      busyPaymentId = null;
    }
  }

  const adjustValid = $derived(
    Number.isFinite(Number(adjustAmount.replace(',', '.'))) &&
      Number(adjustAmount.replace(',', '.')) !== 0 &&
      adjustNote.trim().length > 0,
  );

  async function submitAdjust(): Promise<void> {
    if (!detail || !adjustValid || adjustBusy) return;
    adjustBusy = true;
    adjustError = null;
    try {
      const res = await api.adminReferralAdjust(
        detail.telegramId,
        Number(adjustAmount.replace(',', '.')),
        adjustNote.trim(),
      );
      detail = { ...detail, available: res.available, pending: res.pending };
      adjustAmount = '';
      adjustNote = '';
      await openDetail(detail.telegramId);
      await load();
    } catch {
      adjustError = 'Не вдалось зберегти корекцію';
    } finally {
      adjustBusy = false;
    }
  }

  onMount(async () => {
    if (!$session) {
      replace('/');
      return;
    }
    if (!$session.isAdmin) {
      replace('/home');
      return;
    }
    // Рефералка прихована в цьому середовищі — зведення теж.
    if (!$session.referralsVisible) {
      replace('/admin/settings');
      return;
    }
    await load();
  });
</script>

<div class="screen">
  <div class="admin-header">
    <button type="button" class="back-link" onclick={goBack}>← Назад</button>
    <div class="header-title">Реферальна система</div>
    <a href="#/profile" class="admin-exit">Вийти</a>
  </div>

  <div class="scroll">
    {#if loading}
      <div class="placeholder">Завантажуємо…</div>
    {:else}
      {#if error}
        <div class="placeholder err">{error}</div>
      {/if}

      {#if pending.length > 0}
        <div class="section-title warn-title">Потребують рішення</div>
        <div class="hint">
          Продукт у цих оплатах не розпізнався, тож автоматика не змогла перевірити,
          чи він новий для покупця — і свідомо нічого не нарахувала. Признач комісію
          вручну або залиш як є.
        </div>
        <div class="list">
          {#each pending as p (p.paymentId)}
            <div class="card warn">
              <div class="card-head">
                <div class="card-title">{p.buyerName}</div>
                <div class="card-amount">{p.amount.toFixed(2)} ₴</div>
              </div>
              <div class="card-meta">
                Привела: {p.referrerName} · {formatDate(p.occurredAt)}
              </div>
              <div class="card-meta muted">Оплата: {p.rawProduct ?? 'без назви'}</div>
              <button
                type="button"
                class="btn small"
                disabled={busyPaymentId === p.paymentId}
                onclick={() => accrue(p)}
              >
                {busyPaymentId === p.paymentId ? 'Нараховуємо…' : 'Нарахувати комісію'}
              </button>
            </div>
          {/each}
        </div>
      {/if}

      <div class="section-title">Реферери</div>
      {#if referrers.length === 0}
        <div class="placeholder">Поки що жодного нарахування</div>
      {:else}
        {#if truncated}
          <div class="hint">Показано перші 500 за сумою заробленого.</div>
        {/if}
        <div class="list">
          {#each referrers as r (r.telegramId)}
            <button type="button" class="card clickable" onclick={() => openDetail(r.telegramId)}>
              <div class="card-head">
                <div class="card-title">
                  {r.name}{#if r.username}<span class="muted"> @{r.username}</span>{/if}
                </div>
                <div class="card-amount">{r.totalEarned.toFixed(2)} ₴</div>
              </div>
              <div class="card-meta">
                {r.joined} прив'язано{#if r.code} · код {r.code}{/if}
              </div>
              <div class="chips">
                <span class="chip green">доступно {r.available.toFixed(2)}</span>
                <span class="chip yellow">очікує {r.pending.toFixed(2)}</span>
                {#if r.spent > 0}<span class="chip">витрачено {r.spent.toFixed(2)}</span>{/if}
              </div>
            </button>
          {/each}
        </div>
      {/if}
    {/if}
  </div>
</div>

{#if detail || detailLoading}
  <!-- Кнопка, а не div: так підкладка закривається і з клавіатури, і не
       потребує глушіння a11y-попереджень. -->
  <button
    type="button"
    class="modal-backdrop"
    aria-label="Закрити"
    onclick={() => (detail = null)}
  ></button>
  <div class="modal" role="dialog" aria-modal="true" aria-label="Журнал рефералки">
    {#if detailLoading || !detail}
      <div class="placeholder">Завантажуємо…</div>
    {:else}
      <div class="modal-head">
        <div class="modal-title">{detail.name}</div>
        <button type="button" class="modal-close" onclick={() => (detail = null)}><Icon name="x" /></button>
      </div>
      <div class="modal-body">
        <div class="kv"><span>Код</span><strong>{detail.code ?? '—'}</strong></div>
        <div class="kv">
          <span>Доступно</span><strong>{detail.available.toFixed(2)} ₴</strong>
        </div>
        <div class="kv">
          <span>В очікуванні</span><strong>{detail.pending.toFixed(2)} ₴</strong>
        </div>
        <div class="kv">
          <span>У SendPulse</span>
          <strong class:mismatch={detail.syncedValue !== null && detail.syncedValue !== detail.available}>
            {detail.syncedValue === null ? 'не записувалось' : `${detail.syncedValue.toFixed(2)} ₴`}
          </strong>
        </div>
        {#if detail.syncedValue !== null && detail.syncedValue !== detail.available}
          <div class="hint">
            Дзеркало відстало від журналу — звірка вирівняє його на найближчому тіку крона.
          </div>
        {/if}
        {#if detail.referredBy}
          <div class="kv">
            <span>Кого привела</span>
            <strong>{detail.referredBy.name} · {formatDate(detail.referredBy.at)}</strong>
          </div>
        {/if}

        <div class="section-title">Ручна корекція</div>
        <div class="adjust-row">
          <input
            class="field"
            type="text"
            inputmode="decimal"
            placeholder="Сума, ₴ (мінус — списати)"
            bind:value={adjustAmount}
          />
        </div>
        <input class="field" type="text" placeholder="Причина" bind:value={adjustNote} />
        {#if adjustError}<div class="err-text">{adjustError}</div>{/if}
        <button
          type="button"
          class="btn small"
          disabled={!adjustValid || adjustBusy}
          onclick={submitAdjust}
        >
          {adjustBusy ? 'Зберігаємо…' : 'Застосувати корекцію'}
        </button>

        <div class="section-title">Журнал</div>
        {#if detail.entries.length === 0}
          <div class="placeholder">Порожньо</div>
        {:else}
          <div class="ledger">
            {#each detail.entries as e (e.id)}
              <div class="ledger-row">
                <div class="ledger-main">
                  <span class="ledger-type">{TYPE_LABEL[e.type]}</span>
                  {#if e.referredName}<span class="muted"> · {e.referredName}</span>{/if}
                </div>
                <div class="ledger-amount" class:neg={e.amount < 0}>
                  {e.amount > 0 ? '+' : ''}{e.amount.toFixed(2)} ₴
                </div>
                <div class="ledger-meta">
                  {formatDateTime(e.occurredAt)}
                  {#if e.percent !== null && e.base !== null}
                    · {e.base.toFixed(2)} ₴ × {e.percent}%
                  {/if}
                  {#if e.availableAt}· доступно з {formatDate(e.availableAt)}{/if}
                  {#if e.note}<br />{e.note}{/if}
                </div>
              </div>
            {/each}
          </div>
        {/if}
      </div>
    {/if}
  </div>
{/if}

<style>
  .screen {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    background: var(--bg);
  }
  .admin-header {
    background: linear-gradient(135deg, var(--dark), var(--dark3));
    padding: 14px 18px;
    color: var(--text-light);
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }
  .back-link {
    font-size: 12px;
    color: rgba(251, 246, 236, 0.9);
    background: none;
    border: none;
    cursor: pointer;
    font-family: inherit;
    padding: 4px 0;
  }
  .header-title {
    flex: 1;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
  }
  .admin-exit {
    font-size: 11px;
    opacity: 0.85;
    background: rgba(251, 246, 236, 0.1);
    padding: 5px 11px;
    border-radius: 7px;
    color: var(--text-light);
    text-decoration: none;
  }

  .scroll {
    flex: 1;
    overflow-y: auto;
    padding: 18px 18px 60px;
  }

  .placeholder {
    padding: 16px;
    text-align: center;
    color: var(--text-muted);
    font-size: 12px;
    background: var(--bg2);
    border: 1px dashed var(--border2);
    border-radius: 10px;
  }
  .placeholder.err {
    color: var(--status-inactive);
    margin-bottom: 14px;
  }

  .section-title {
    font-size: 12px;
    font-weight: 700;
    color: var(--text);
    margin: 18px 0 8px;
  }
  .section-title.warn-title {
    color: var(--status-expiring);
    margin-top: 0;
  }
  .hint {
    font-size: 11px;
    color: var(--text-muted);
    line-height: 1.45;
    margin-bottom: 10px;
  }

  .list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .card {
    display: block;
    width: 100%;
    text-align: left;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 12px 14px;
    font-family: inherit;
    color: inherit;
  }
  .card.clickable {
    cursor: pointer;
  }
  .card.warn {
    background: var(--warn-bg);
    border-color: var(--warn-border);
  }
  .card-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 10px;
  }
  .card-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .card-amount {
    flex-shrink: 0;
    font-size: 14px;
    font-weight: 700;
    color: var(--accent-text);
  }
  .card-meta {
    font-size: 11px;
    color: var(--text-muted);
    margin-top: 4px;
  }
  .muted {
    color: var(--text-muted);
  }
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
  }
  .chip {
    font-size: 10.5px;
    font-weight: 600;
    padding: 3px 9px;
    border-radius: 999px;
    background: var(--bg);
    color: var(--text-muted);
  }
  .chip.green {
    color: var(--status-active);
    background: var(--green-light, var(--green-light));
  }
  .chip.yellow {
    color: var(--status-expiring);
    background: var(--warn-bg);
  }

  .btn {
    padding: 10px 14px;
    border-radius: 10px;
    border: none;
    background: linear-gradient(135deg, var(--pu), var(--pu3));
    color: var(--text-light);
    font-size: 12.5px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
  }
  .btn.small {
    margin-top: 10px;
    width: 100%;
  }
  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 50;
    border: none;
    padding: 0;
    cursor: pointer;
  }
  .modal {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    max-height: 88vh;
    overflow-y: auto;
    background: var(--bg);
    border-radius: 16px 16px 0 0;
    z-index: 51;
    padding-bottom: 24px;
  }
  .modal-head {
    position: sticky;
    top: 0;
    background: var(--bg);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 18px 10px;
    border-bottom: 1px solid var(--border);
  }
  .modal-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--text);
  }
  .modal-close {
    background: none;
    border: none;
    font-size: 15px;
    color: var(--text-muted);
    cursor: pointer;
  }
  .modal-body {
    padding: 12px 18px 0;
  }

  .kv {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 12.5px;
    padding: 7px 0;
    border-bottom: 1px solid var(--border);
    color: var(--text-muted);
  }
  .kv strong {
    color: var(--text);
    font-weight: 600;
    text-align: right;
  }
  .kv strong.mismatch {
    color: var(--status-expiring);
  }

  .field {
    width: 100%;
    border: 1px solid var(--border2);
    border-radius: 10px;
    padding: 10px 12px;
    font-size: 13px;
    color: var(--text);
    background: var(--bg);
    font-family: inherit;
    outline: none;
    margin-bottom: 8px;
  }
  .field:focus {
    border-color: var(--outline);
  }
  .adjust-row {
    display: flex;
    gap: 8px;
  }
  .err-text {
    font-size: 11.5px;
    color: var(--status-inactive);
    margin-bottom: 8px;
  }

  .ledger {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .ledger-row {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 2px 10px;
    padding: 10px 12px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 10px;
  }
  .ledger-type {
    font-size: 12px;
    font-weight: 600;
    color: var(--text);
  }
  .ledger-amount {
    font-size: 13px;
    font-weight: 700;
    color: var(--status-active);
    text-align: right;
  }
  .ledger-amount.neg {
    color: var(--status-inactive);
  }
  .ledger-meta {
    grid-column: 1 / -1;
    font-size: 10.5px;
    color: var(--text-muted);
    line-height: 1.45;
  }
</style>
